import React, { useEffect, useMemo, useRef, useState } from "react";
import { StockTypeUpdateRowsData } from "../../../types/types";
import {
  getMergedCatalogueAndBatchIdData,
  StockTypeUpdateColumnsMetadata,
  setStockTypeUpdateDataInDb,
  sumUpdatedQuantities,
} from "./StockTypeUpdateUtils";
import { Loader } from "../../../components/loader/Loader";
import CustomDataGrid from "../../../components/dms_common/CustomDataGrid";
import Header from "../../../components/dms_common/Header";
import CreateNewStockTypeUpdateForm from "./NewStockTypeUpdateForm";
import {
  defaultSearchFunction,
  filterData,
  updateDatesInLocalStorage
} from "../../../components/dms_common/DmsCommonServices";
import { Alert, Button, Snackbar, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getUTCDate } from "../../../services/manageOrderService";
import {
  isGapMoreThanNDays,
  isStartDateIsGreaterThanEndDate,
} from "../../../utils/calenderUtils";
import { downloadAllReports } from "../../../services/manageReportService";
import { getDataFromLocalDb } from "../../../services/dbService";
import "./StockTypeUpdate.css";
import { getConfigData } from "../../../services/configurationService";
import ScreenOverlay from "../../../components/dms_common/ScreenOverlay";
import { Product } from "../../createSecondaryOrder/models";
import CustomGenericModal from "../../../components/dms_common/CustomGenericModal";
import { GenericModal, getGenericModalProps, initialGenericModalProps } from "../../viewOrder/ViewOrderUtils";
import { logAnalyticsEvent, logLoadTrace } from "../../../firebase";
import { StatusFilterMenuItems } from "../../../types/types";
import { getDistributorList, setRealTimeStockDataInDb } from "../RealTimeStock/RealTimeStockUtils";
import EmptyPage from "../../claimsNew/components/SelectClaimType";
import realTimeEmptyImage from "../../../assets/image/svg/realTimeEmptyImage.svg";

const StockTypeUpdate = ({isCompanyUser} : {isCompanyUser: boolean}) => {
  const [lastRefreshed, setLastRefreshed] = useState<Dayjs>(dayjs());
  const [openNewStockTypeUpdateForm, setOpenNewStockTypeUpdateForm] = useState(false);
  const [data, setData] = useState<StockTypeUpdateRowsData[]>([]);
  const [rows, setRows] = useState<StockTypeUpdateRowsData[]>([]);
  const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(7, "day"));
  const [endDate, setEndDate] = useState<Dayjs>(dayjs());
  const [isLoading, setIsLoading] = useState(false);
  const [skuProductDescriptionMap, setSkuProductDescriptionMap] = useState<Record<string, string>>(
    {}
  );
  const [skuUomMap, setSkuUomMap] = useState<Record<string, string>>({});
  const [existingBatchIds, setExistingBatchIds] = useState<Record<string, number>>({});
  const [searchedValue, setSearchedValue] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const stockTypeUpdateReportRef = useRef<string | null>(null);
  const [genericModalProps, setGenericModalProps] = useState<GenericModal>(initialGenericModalProps);
  const [resetSearch, setResetSearch] = useState(false);
  const [distributors, setDistributors] = useState<StatusFilterMenuItems[]>([]);
  const [selectedDistributor, setSelectedDistributor] = useState<string>("");
  const [noDistributorsForCompanyUser, setNoDistributorsForCompanyUser] = useState<boolean>(false);

  const feature = "Stock";
  const pageID = "StockTypeUpdate";

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSearchValueChange = (newValue: string) => {
    setSearchedValue(newValue);
  };

  const filteredData = useMemo(() => {
    return filterData(data, searchedValue.toLowerCase(), defaultSearchFunction);
  }, [searchedValue, data]);

  useEffect(() => {
    setRows(filteredData);
  }, [filteredData]);

  const getInitialTableData = async (distributorId?: string) => {
    const startDate = dayjs().subtract(7, "day")
    const endDate = dayjs()
    setStartDate(startDate);
    setEndDate(endDate);
    const start = getUTCDate(startDate, "start");
    const end = getUTCDate(endDate, "end");
    await getAllStockTypeData(start, end, distributorId);
  };

  const getAllStockTypeData = async (startDate: string, endDate: string, distributorId?: string) => {
    setLastRefreshed(dayjs());
    setResetSearch(true); 
    setTimeout(() => setResetSearch(false), 0); 
    try {
      await setStockTypeUpdateDataInDb(startDate, endDate, distributorId);
      const response = await getDataFromLocalDb("StockTypeUpdateData");
      let stockIdWiseUpdatedData: StockTypeUpdateRowsData[] = [];
      let stockIdUpdatedCountMap: Record<string, number> = {};
      
      // Process all items with async sumUpdatedQuantities
      const processedItems = await Promise.all(
        response.data.map(async (item: any) => {
          const { totalUpdatedPieceQty, totalUpdatedCaseQty, totalUpdatedOtherQty } =
            await sumUpdatedQuantities(item.skuDetails, item.skuCode);
          
          return {
            requestId: item.requestId,
            id: item.requestId,
            skuCode: item.skuCode,
            skuName: item.skuName,
            updatedPieceQuantity: totalUpdatedPieceQty.toString(),
            updatedCaseQuantity: totalUpdatedCaseQty.toString(),
            updatedOtherQuantity: totalUpdatedOtherQty.toString(),
            adjustmentDate: item?.adjustmentDate,
            skuDescription: "",
            fromStatus: item.fromStatus,
            batchIdCount: item?.skuDetails?.length ?? 0,
            skuDetails: item.skuDetails,
          };
        })
      );
      
      stockIdWiseUpdatedData = processedItems;
      
      // Build the batch ID count map
      response.data.forEach((item: any) => {
        if (stockIdUpdatedCountMap[item.batchId]) {
          stockIdUpdatedCountMap[item.batchId] += 1;
        } else {
          stockIdUpdatedCountMap[item.batchId] = 1;
        }
      });

      const productsData = await getDataFromLocalDb("Product");
      const productsMap: Record<string, Product> = productsData?.data?.[0]?.data ?? {};
      
      // Get stock data to filter SKUs
      await setRealTimeStockDataInDb("", "", distributorId);
      const stockData = await getDataFromLocalDb("RealTimeStockData");
      const stockSkuCodes = new Set<string>();
      stockData?.data?.forEach((item: any) => {
        if (item.skuCode) {
          // Check if SKU has available quantity > 0 in any quantity type (piece, case, other)
          const hasAvailableQty = 
            (item.pieceQty?.availableQty && item.pieceQty.availableQty > 0) ||
            (item.caseQty?.availableQty && item.caseQty.availableQty > 0) ||
            (item.otherQty?.availableQty && item.otherQty.availableQty > 0);
          
          if (hasAvailableQty) {
            stockSkuCodes.add(item.skuCode);
          }
        }
      });
      
      const skuProductMap: Record<string, string> = {};
      const tempSkuUomMap: Record<string, string> = {};
      const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
      
      // Only include SKUs that exist in both Product DB and have stock data
      Object.values(productsMap).forEach((item) => {
        if (stockSkuCodes.has(item.batchCode)) {
          skuProductMap[item.batchCode] = item.skuDescription;
          tempSkuUomMap[item.batchCode] = ConfigDictionary.orderUomCheck === "true" ? (item?.uom ?? "") : "all";
        }
      });
      
      setSkuProductDescriptionMap(skuProductMap);
      setSkuUomMap(tempSkuUomMap);
      const mergedData = getMergedCatalogueAndBatchIdData(stockIdWiseUpdatedData, productsMap);
      setRows(mergedData);
      setData(mergedData);
      setExistingBatchIds(stockIdUpdatedCountMap);
      logAnalyticsEvent( "stock_type_update_viewed", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "total_skus_displayed": mergedData.length
      });
      
    } catch (error) {
      setRows([]);
      setData([]);
      setGenericModalProps(getGenericModalProps(`error`, `Error while fetching stock type data`));
      logAnalyticsEvent( "stock_type_update_viewed", {
        "distributor_id": JSON.parse(localStorage.authContext).user.loginId,
        "total_skus_displayed": 0
      });
    }
  };

  const init = async (distributorId?: string) => {
    await getInitialTableData(distributorId);
    const stockTypeUpdateReportIdConfig = await getConfigData(
      "dms_configuration",
      "stockTypeUpdateReportId"
    );
    stockTypeUpdateReportRef.current = stockTypeUpdateReportIdConfig[0]?.configValue ?? "";
  };

  useEffect(() => {
    setSearchedValue("");
    if (!openNewStockTypeUpdateForm) {
      if (isCompanyUser) {
        fetchDistributors();
      } else {
        const currentUser = JSON.parse(localStorage.authContext).user;
        setSelectedDistributor(currentUser.loginId);
        logLoadTrace(init, "stock_type_update_page_load_time");
      }
    }
  }, [openNewStockTypeUpdateForm, isCompanyUser]);

  useEffect(() => {
    const interval = setInterval(() => {
      getInitialTableData(selectedDistributor);
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [selectedDistributor]);

  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
  const validDateRange = ConfigDictionary.dateRangeConfig ?? 31;
  function canWeMakeRequest() {
    if (startDate && endDate && isGapMoreThanNDays(startDate, endDate, validDateRange)) {
      setGenericModalProps(getGenericModalProps(`positive`, `You can't select date range more than ${validDateRange} days!`));
      return false;
    }
    if (startDate && endDate && isStartDateIsGreaterThanEndDate(startDate, endDate)) {
      setGenericModalProps(getGenericModalProps(`positive`, `To Date should be greater than from date.`));
      return false;
    }
    updateDatesInLocalStorage(startDate, endDate, feature, pageID);
    return true;
  }

  const handleApply = async () => {
    if (canWeMakeRequest()) {
      const start = getUTCDate(startDate, "start");
      const end = getUTCDate(endDate, "end");
      getAllStockTypeData(start, end, selectedDistributor);
    }
  };

  const handleClearAll = () => {
    const date = dayjs();
    const start = getUTCDate(date, "start");
    const end = getUTCDate(date, "end");
    setStartDate(dayjs());
    setEndDate(dayjs());
    updateDatesInLocalStorage(date, date, feature, pageID);
    getAllStockTypeData(start, end, selectedDistributor);
  };

  const fetchDistributors = async () => {
    setIsLoading(true);
    try {
      const distributorsList = await getDistributorList();
      setDistributors(distributorsList);
      
      if (distributorsList.length > 0) {
        setNoDistributorsForCompanyUser(false);
        const firstDistributorId = distributorsList[0].id;
        setSelectedDistributor("");
        
        logLoadTrace(() => init(firstDistributorId), "stock_type_update_page_load_time");
      } else {
        setNoDistributorsForCompanyUser(true);
      }
    } catch (error) {
      console.error("Error fetching distributors list:", error);
      logLoadTrace(init, "stock_type_update_page_load_time");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDistributorChange = (distributorId: string) => {
    setSelectedDistributor(distributorId);
    logLoadTrace(() => init(distributorId), "real_time_stock_distributor_change_time");
  };

  return (
    <div className="StockTypeUpdate">
      <CustomGenericModal
          type={genericModalProps?.type}
          message={genericModalProps?.message}
          modalOpen={genericModalProps?.open}
          onClose={() => setGenericModalProps(initialGenericModalProps)}
        />
      <ScreenOverlay apply={isLoading} />
      {!openNewStockTypeUpdateForm ? (
        <>
          <Header
            title={"Stock Type Update"}
            searchFunction={handleSearchValueChange}
            createBtnLabel={"New Stock Type Update"}
            handleCreateBtnClick={() => setOpenNewStockTypeUpdateForm(true)}
            createBtnDisabled={isCompanyUser && !selectedDistributor}
            downloadReportIcon={true}
            downloadReportOnClick={() =>{
              logAnalyticsEvent( "stock_type_report_downloaded", {
                "distributor_id": isCompanyUser ? selectedDistributor : JSON.parse(localStorage.authContext).user.loginId,
              });
              downloadAllReports(
                stockTypeUpdateReportRef.current,
                setSnackbarOpen,
                startDate,
                endDate,
                isCompanyUser ? "loggedInUser" : undefined,
                isCompanyUser ? selectedDistributor : undefined,
                isCompanyUser ? selectedDistributor : undefined
              )
            }
            }
            resetSearch={resetSearch}
            lastRefreshed={lastRefreshed}
            showDistributorFilter={isCompanyUser}
            onDistributorChange={handleDistributorChange}
            distributorList={distributors}
            enableTruncation={true}
          />
          <div className="dms_toolbar">
            <div className="stocks_support_utility_dates">
              <LocalizationProvider dateAdapter={AdapterDayjs} className="manage_order_dateEnter">
                <DatePicker
                  label="From Date"
                  inputFormat="DD-MM-YYYY"
                  value={startDate}
                  maxDate={dayjs()}
                  onChange={(value) => {
                    if (value) {
                      setStartDate(value);
                    }
                  }}
                  renderInput={(props) => <TextField {...props} disabled size="small" />}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="To Date"
                  inputFormat="DD-MM-YYYY"
                  value={endDate}
                  maxDate={dayjs()}
                  onChange={(value) => {
                    if (value) {
                      setEndDate(value);
                    }
                  }}
                  renderInput={(props) => <TextField {...props} disabled size="small" />}
                />
              </LocalizationProvider>
              <Button
                variant="contained"
                sx={{
                  textTransform: "none",
                  backgroundColor: "#237C9D",
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "none",
                  },
                }}
                onClick={handleApply}
                disabled={isCompanyUser && !selectedDistributor}
              >
                Apply
              </Button>
              <Button
                variant="outlined"
                sx={{
                  textTransform: "none",
                  backgroundColor: "#f0fbff",
                  boxShadow: "none",
                  "&:hover": {
                    boxShadow: "none",
                  },
                }}
                onClick={handleClearAll}
                disabled={isCompanyUser && !selectedDistributor}
              >
                Reset
              </Button>
            </div>
          </div>
          {isLoading ? (
            <Loader sx={{ position: "absolute", top: "45%" }} />
          ) : noDistributorsForCompanyUser ? (
            <>
              <EmptyPage
                height={87}
                text={
                  <div style={{ textAlign: "center", whiteSpace: "pre-wrap" }}>
                    No distributors exist for this company user.
                  </div>
                }
                imgSrc={realTimeEmptyImage}
              />
            </>
          ) : isCompanyUser && !selectedDistributor ? (
            <>
              <EmptyPage
                height={87}
                text={
                  <div style={{ textAlign: "center", whiteSpace: "pre-wrap" }}>
                    Please select a Distributor from the drop-down to proceed.
                  </div>
                }
                imgSrc={realTimeEmptyImage}
              />
            </>
          ) : (
            <CustomDataGrid
              searchedValue={searchedValue}
              rows={rows}
              columnsMetaData={StockTypeUpdateColumnsMetadata}
              tableWidth={0.765}
              redirectUrl={isCompanyUser ? "/stocks/companyStockTypeUpdate/StockTypeUpdateSkuDetailsScreen" : "/stocks/distributorStockTypeUpdate/StockTypeUpdateSkuDetailsScreen"}
              noDataMessage="No Stock data found."
              enableRowClickRedirect={true}
            />
          )}
        </>
      ) : (
        <CreateNewStockTypeUpdateForm
          setOpenNewStockTypeUpdateForm={setOpenNewStockTypeUpdateForm}
          skuProductDescriptionMap={skuProductDescriptionMap}
          skuUomMap={skuUomMap}
          selectedDistributor={selectedDistributor}
          isCompanyUser={isCompanyUser}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Your report will be downloaded shortly.
        </Alert>
      </Snackbar>
    </div>
  );
};

export default StockTypeUpdate;
